import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { horizonAfter, useReadVersion } from '../../components/ReportingControls';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import Tabs from '../../components/Tabs';
import SegmentedToggle from '../../components/SegmentedToggle';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { getPlRevenueView, getPlView, setLineForecast, generateForecast, updateLine, createLine } from '../../api/financials_pl';
import ServicePlanPage from './ServicePlanPage';
import DirectCostsPage from '../financials/DirectCostsPage';
import RevenueMarginsPage from './RevenueMarginsPage';
import SalesReceivablesPage from './SalesReceivablesPage';
import { getSalesPlan, putSalesPlanSection } from '../../api/salesPlan';
import {
  defaultModel, sanitize, yearsOf, closedOf,
  entryOf, detailsOf, monthsOf, annualOf, realizedOf,
  qtyFromCa, detailRealized, detailMonth, driverRealized,
  isPctDetail, detailAnnual,
  lineAnnual, lineRealized, lineMonth, allDrivers,
  lineEntry, detailEntry, adjEntry, adjPlanOf, addDetail, delDetail, renameDetail,
  entryRule, ruleValue, applyEntryRule,
  attachDriver, detachDriver, setDetailField, setDetailQty, setDriverRealMonth, setDriverRealTotal, setDriverPlanMonth,
  joinedPlanByLine, productPlanLines, productPlanRevenueMonth, heldVarRate,
  productPlanOf, productCardRevenueMonth,
  servicePlanOf, serviceCardRevenueMonth, servicePlanRevenueMonth, plannedLines,
  serviceRowRevenueMonth, productRowQtyMonth, productRowPriceAt,
  variablesOf, parseRef, addVariable, delVariable, setVariableField, setVariableRate, copyYearForward,
  setAnnual, setRealized, setMonth,
  cardBilledMonth, prepaidBilledAt, hourbankBilledMonth, tradeRowCaMonth,
} from './atterrissage';
import './Atterrissage.css';

/* Proto « Atterrissage » (module Ventes) — PROTOTYPE autonome, localStorage.
   On travaille D'ABORD dans le CA (lignes Finance + détails). Un détail porte
   éventuellement un INDUCTEUR (＋ inducteur devant la ligne) → il apparaît dans la
   card Inducteurs, en dessous. Toggle Plan annuel / mensuel ; colonne Réalisé ;
   bascule CHF/kCHF. ⚠️ Le Réalisé devra venir de Finance (compta). */

const nf = new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 });
const fmt = (n) => nf.format(Math.round(Number(n) || 0)).replace(/’|,/g, "'");
const cleanNum = (v) => { const x = parseFloat(String(v).replace(/['\s’]/g, '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };
const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
// [valeur stockée, libellé du menu] — l'affichage en card passe par unitLabel().
const UNIT_OPTIONS = [['jours-hommes', 'Jours-hommes (JH)'], ['heures', 'heures'], ['EPT capa', 'EPT capa'], ['EPT total', 'EPT total']];
const unitLabel = (u) => (u === 'jours-hommes' ? 'JH' : u);

/* Lecture seule : le périmètre Ventes en `r` autorise à CONSULTER le plan, pas à
   l'écrire. Sans ce garde-fou, la saisie était acceptée à l'écran et la
   sauvegarde échouait en silence (403 avalé par un .catch vide) : l'utilisateur
   croyait avoir enregistré, et perdait son travail au rechargement. */
export const AtlReadOnlyCtx = createContext(false);

function Num({ value, onCommit, mult = 1, muted = false, onPasteVals }) {
  const readOnly = useContext(AtlReadOnlyCtx);
  const [draft, setDraft] = useState(null);
  const [foc, setFoc] = useState(false);
  const disp = (Number(value) || 0) * mult;
  const shown = draft != null ? draft : foc ? String(value == null || value === '' ? '' : disp) : (value === '' || value == null ? '' : fmt(disp));
  const commit = () => { setFoc(false); if (draft == null) return; onCommit(cleanNum(draft) / mult); setDraft(null); };
  // Collage type Excel : une rangée (tabulations) OU une colonne (retours
  // ligne) remplit les cellules suivantes ; une seule valeur → champ normal.
  const onPaste = (e) => {
    if (!onPasteVals) return;
    const txt = e.clipboardData.getData('text');
    const parts = txt.split(/[\t\r\n;]+/).map((x) => x.trim()).filter((x) => x !== '');
    if (parts.length < 2) return;
    e.preventDefault();
    onPasteVals(parts.map((x) => cleanNum(x) / mult));
    setDraft(null); setFoc(false); e.currentTarget.blur();
  };
  return (
    <input className={`atl-in${muted ? ' atl-in--muted' : ''}`} value={shown} readOnly={readOnly}
      onFocus={() => !readOnly && setFoc(true)}
      onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPaste={onPaste}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}
function Txt({ value, onCommit, placeholder, cls = '' }) {
  const readOnly = useContext(AtlReadOnlyCtx);
  const [draft, setDraft] = useState(null);
  const shown = draft != null ? draft : (value ?? '');
  return (
    <input className={`atl-in atl-in--text ${cls}`} value={shown} placeholder={placeholder}
      readOnly={readOnly}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft != null) { onCommit(draft); setDraft(null); } }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}
/* Clés du document plan possédées par CETTE page (Valeur ajoutée planifiée) :
   ses lignes/variables, et l'allocation des coûts de cards aux lignes de
   charges directes. Les cards elles-mêmes appartiennent aux pages Plan
   produits / Plan services — on ne les réécrit jamais d'ici. */
const PLAN_OWNED = ['data', 'variables', 'cardCostLines'];

function deepMutable(v) {
  if (Array.isArray(v)) return v.map(deepMutable);
  if (v && typeof v === 'object') { const o = {}; for (const k in v) o[k] = deepMutable(v[k]); return o; }
  return v;
}
/* Card repliable : pastille ＋/− devant le titre (clic sur l'icône OU le titre). */
function Card({ title, note, right, children }) {
  const [folded, setFolded] = useState(false);
  return (
    <div className="atl-card">
      <div className="atl-card__head">
        <h3 className="atl-card__title" onClick={() => setFolded(!folded)}>{title}</h3>
        {note && <span className="atl-card__note">{note}</span>}
        {right && <span className="atl-card__right">{right}</span>}
      </div>
      {!folded && <div className="atl-card__body">{children}</div>}
    </div>
  );
}

/* DEUX SECTIONS sur une même page (2026-09-12) : « Revenus » (`variant`
   revenue : le plan des ventes et sa prévision, ce que Finance lit) et
   « Ancienne prévision » (les plans produits et services par cards, derrière
   un panneau interdit le temps que le nouveau parcours les remplace). */
/* IMPACTS FINANCIERS (2026-09-12) : la prévision du revenu et ce qu'il coûte,
   une seule section ; l'Ancienne prévision garde le plan des ventes et les
   cards (le Plan services héberge les produits). */
const ONGLETS = { revenue: ['margins', 'receivables'], legacy: ['plan', 'services', 'costs'] };   // Impacts financiers : Revenus & Marges, Débiteurs ; Coûts des ventes est retourné à l'Ancienne prévision (2026-09-12)   // le plan des ventes rejoint l'ancienne prévision, en tête (2026-09-12)
export default function AtterrissagePlanPage({ activeTenant, year, addToast, canWrite = true, canRead = true, variant = 'legacy', canWriteCosts = canWrite }) {
  const { t } = useTranslation();
  const onglets = ONGLETS[variant] || ONGLETS.legacy;
  const titre = variant === 'revenue' ? t('nav:items.sales-revenue', 'Impacts financiers') : t('scenarii:atterrissage.title', 'Ancienne prévision');
  const tid = activeTenant?.id;
  const y0 = Number(year) || 2026;
  const lue = useReadVersion();
  const [model, setModel] = usePersistentValueLocal(`atterrissage_${tid || 'none'}`, defaultModel(y0));
  const [view, setView] = useState('m0');
  const [win, setWin] = useState('1');       // fenêtre mensuelle : 1 année ou 2 années côte à côte
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);   // bande de contexte, sur la ligne de l'exercice
  const [billOpen, setBillOpen] = useState(new Set());
  const [revOpen, setRevOpen] = useState(new Set());
  const [hubTab, setHubTab] = useState(onglets[0]);   // le premier onglet de la variante ouvre
  const [revLines, setRevLines] = useState(null);
  // Lignes dont la prévision est produite par une RÈGLE du compte de résultat :
  // toute méthode autre que « Plan des ventes » (qui, elle, vient d'ici).
  const finRuled = React.useMemo(() => new Set(
    (revLines || []).filter((l) => l.forecast_method && l.forecast_method !== 'sales_plan')
      .map((l) => l.id),
  ), [revLines]);
  const [fin, setFin] = useState(null); // { real:{id:kchf YTD}, months:{id:[12] kCHF}, closed } — compta Finance
  const [prevFin, setPrevFin] = useState(null); // { real:{id:kchf FY} } — compta N−1 (colonne 2025, si données)
  const [open, setOpen] = useState({});
  const [drvModal, setDrvModal] = useState(null); // { lineId, did, form }
  const [indMenu, setIndMenu] = useState(null); // { lineId, did } — menu « + inducteur »
  const [varModal, setVarModal] = useState(null); // { id, form } — revenu variable
  const [openPlans, setOpenPlans] = useState(new Set()); // cards Plan produits/services dépliées
  const [cogsFin, setCogsFin] = useState([]);            // lignes de charges directes (destinations)
  const [cogsLoaded, setCogsLoaded] = useState(false);
  /* Sauvegarde IMMÉDIATE du plan (hors temporisation) : le bloc Coûts directs
     l'appelle avant d'appliquer une règle, sinon la ligne de base serait
     régénérée depuis un plan calculé périmé — la règle semblait alors fausse
     (constat 2026-08-16 : 100 % d'une ligne donnait d'autres montants). */
  const syncPlan = React.useCallback(async () => {
    if (!tid || !canWrite) return;
    await putSalesPlanSection(tid, model, PLAN_OWNED,
      (merged) => ((revLines || []).length
        ? joinedPlanByLine(merged, revLines.map((l) => l.id))
        : merged.computed)).catch(() => {});
  }, [tid, canWrite, model, revLines]);

  // Incrémenté après chaque régénération : le bloc Coûts directs se recharge.
  const [fcStamp, setFcStamp] = useState(0);
  /* Pose le coût matériel d'une FAMILLE de cards sur une ligne de charges
     directes (2026-08-16). Une famille = une seule ligne : on détache d'abord
     celle qui la portait, sinon deux lignes recevraient la même série. */
  /* Alloue UNE card à une ligne de charges directes (2026-08-16) : la card
     porte sa ligne (`costLine`), la ligne lit la somme des cards qu'on lui a
     allouées (méthode `sales_cards`). Plusieurs cards par ligne = cumul. */
  const allocCard = async (cardId, svc, lineId) => {
    if (!tid || !canWrite) return;
    const prevLine = (model.cardCostLines || {})[cardId] || null;
    const next = deepMutable(model);
    if (!next.cardCostLines) next.cardCostLines = {};
    if (lineId) next.cardCostLines[cardId] = lineId;
    else delete next.cardCostLines[cardId];
    setModel(next);
    // Le document est enregistré AVANT la génération : la ligne doit lire la
    // nouvelle allocation, pas la précédente.
    await putSalesPlanSection(tid, next, PLAN_OWNED,
      (merged) => ((revLines || []).length
        ? joinedPlanByLine(merged, revLines.map((l) => l.id))
        : merged.computed)).catch(() => {});
    // La ligne d'accueil lit les cards ; l'ancienne se recalcule aussi (elle en
    // a une de moins).
    for (const id of [lineId, prevLine].filter((v, i2, arr) => v && arr.indexOf(v) === i2)) {
      if (id === lineId) await updateLine(tid, id, { forecast_method: 'sales_cards' });
      await generateForecast(tid, { year: model.currentYear, line_id: id });
    }
    setFcStamp((s2) => s2 + 1);
  };
  const [copyModal, setCopyModal] = useState(null); // { sel: {lineId: bool}, dc: bool } — copie N → N+1
  // Copie N → N+1 des CHARGES DIRECTES : les valeurs vivent côté serveur (P&L),
  // on relit l'année source et on réécrit les 12 mois de l'année cible
  // (2026-08-16 — la copie ne portait que sur les lignes de revenus).
  const copyDirectCosts = async (from, to) => {
    const r = await getPlView(tid, from).catch(() => null);
    const v = r && !r.error ? r.data : null;
    if (!v) return;
    const cogs = (v.lines || []).filter((l) => l.section === 'cogs' && !l.hidden);
    const rows = {};
    (v.rows || []).forEach((x) => { if (x.type === 'detail') rows[x.code] = x; });
    for (const l of cogs) {
      for (let m = 1; m <= 12; m++) {
        const amount = Number(rows[l.id]?.months?.[String(m)] || 0);
        if (!amount) continue;
        // eslint-disable-next-line no-await-in-loop
        await setLineForecast(tid, { year: to, reporting_line_id: l.id, month: m, amount });
      }
    }
  };
  const [nameW, setNameW] = usePersistentValueLocal(`atterrissage_namew_${tid || 'none'}`, 224); // largeur colonne libellé (px), réglable

  useEffect(() => { setModel(sanitize(model, y0)); /* eslint-disable-next-line */ }, []);

  // Sans droit de lecture sur Ventes : on PURGE la copie locale. Le cache
  // navigateur ne doit jamais servir de contournement — il survivait au
  // changement de rôle et à la déconnexion, et rendait visibles des chiffres
  // que le serveur refuse (403).
  useEffect(() => {
    if (canRead || !tid) return;
    try {
      window.localStorage.removeItem(`atterrissage_${tid}`);
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith(`atl_bak_${tid}_`))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (e) { /* quota / mode privé : rien à faire */ }
  }, [canRead, tid]);
  // L'horizon de la version de plan lue (2026-10-11), années futures N+1..N+h :
  // nb de colonnes = h + 1 (année en cours incluse). Réduire MASQUE, ne supprime pas.
  const horizonY = horizonAfter(lue, y0) + 1;
  useEffect(() => {
    if (model.nbYears !== horizonY) update((d) => { d.nbYears = horizonY; });
    // eslint-disable-next-line
  }, [horizonY]);
  // ── Persistance SERVEUR (2026-08-09) ────────────────────────────────────
  // Le plan vivait uniquement dans le navigateur. Il est désormais stocké côté
  // serveur ; le localStorage reste en cache local (affichage immédiat, et filet
  // si le réseau tombe). Au premier chargement d'un client dont le serveur ne
  // connaît encore rien, la saisie locale existante est REPRISE telle quelle —
  // sans quoi le travail déjà fait disparaîtrait de l'écran.
  const syncedFor = useRef(null);
  const serverHadContent = useRef(false);
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!tid) return undefined;
    let alive = true;
    syncedFor.current = null;
    getSalesPlan(tid)
      .then((r) => {
        if (!alive) return;
        const remote = r?.error ? null : r?.data?.data;
        // Le document est PARTAGÉ avec le Plan produits : un client qui ne
        // planifie QUE des produits n'a rien dans `data`, et sa copie serveur
        // était ignorée ici — la page ne voyait pas ses catégories, et pire,
        // repoussait un modèle local sans elles (perte silencieuse).
        if (remote && (Object.keys(remote.data || {}).length || (remote.productPlan?.cards || []).length || (remote.servicePlan?.cards || []).length)) {
          setModel(sanitize(remote, y0));                 // le serveur fait foi
        } else if (Object.keys(model?.data || {}).length) {
          // Reprise du local vers le serveur : jamais en lecture seule, sinon
          // on pousserait au client des chiffres qu'on n'a pas le droit d'écrire.
          // Reprise du local : par SECTION, jamais le document entier.
          if (canWrite) putSalesPlanSection(tid, model, PLAN_OWNED).catch(() => {});
        }
        // `unwrap` ne rejette JAMAIS (contrat {data,error}) : sans ce test, une
        // lecture en échec armait quand même la sauvegarde et le modèle local
        // (souvent vide) écrasait le plan du serveur — cause des cards
        // disparues (2026-08-16). Lecture KO → on n'arme pas.
        if (!r?.error) {
          serverHadContent.current = !!(remote && (Object.keys(remote.data || {}).length
            || (remote.productPlan?.cards || []).length || (remote.servicePlan?.cards || []).length));
          syncedFor.current = tid;
        }
      })
      // Échec de lecture serveur → on N'ARME PAS la sauvegarde : sinon la page
      // repousserait un modèle local périmé par-dessus la copie serveur.
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid]);

  // Totaux joints au document : le PLAN par ligne de revenus et par mois, en CHF
  // (revenueByLine, + cogsByLine du Plan produits). Le calcul vit en JS — le
  // rejouer côté serveur créerait un second moteur. UNE implémentation partagée
  // avec la page Plan produits : `joinedPlanByLine` (moteur atterrissage.js).
  // On envoie le plan SEUL : le réel, Finance l'a déjà.

  // Écriture différée : la saisie est au clavier, on n'écrit pas à chaque frappe.
  useEffect(() => {
    if (!tid || syncedFor.current !== tid || !canWrite) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Garde-fou : un modèle VIDE ne remplace jamais un plan serveur qui avait
      // du contenu (perte constatée le 2026-08-16).
      const empty = !Object.keys(model.data || {}).length
        && !(model.productPlan?.cards || []).length
        && !(model.servicePlan?.cards || []).length;
      if (empty && serverHadContent.current) return;
      putSalesPlanSection(tid, model, PLAN_OWNED,
        // La jointure n'est recalculée QUE si les lignes de revenus sont
        // chargées : sinon `computed` partait vide et Finance ne voyait plus le
        // plan (règle « % d'une ligne » à 0 — constat 2026-08-16).
        (merged) => ((revLines || []).length
          ? joinedPlanByLine(merged, revLines.map((l) => l.id))
          : merged.computed))
        .then((r) => {
          // Le plan a bougé → on régénère les lignes de revenus qu'il pilote.
          // Le serveur cascade ensuite sur les charges en « % d'une ligne »
          // (achats = x % des ventes) : changer les ventes met les achats à
          // jour, au lieu de les laisser sur l'ancienne valeur (2026-08-16).
          if (r?.error) return;
          // Régénération COMPLÈTE de l'année : les lignes pilotées par le plan
          // ne sont pas toutes « possédées » par une card (le résiduel
          // « Autres … » ne l'est pas), et une régénération ligne à ligne les
          // manquait — les achats ne suivaient donc pas les ventes
          // (constat 2026-08-16). Les lignes SANS règle ne sont pas touchées.
          generateForecast(tid, { year: cur })
            .then(() => setFcStamp((n) => n + 1))
            .catch(() => {});
        })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [model, tid, revLines]);

  // Filet local conservé : une copie datée par jour et par client, purge au-delà
  // de 10 jours. Restauration : copier `atl_bak_<tid>_<date>` vers `atterrissage_<tid>`.
  useEffect(() => {
    if (!tid) return;
    try {
      const day = new Date().toISOString().slice(0, 10);
      const key = `atl_bak_${tid}_${day}`;
      const raw = window.localStorage.getItem(`atterrissage_${tid}`);
      if (raw && !window.localStorage.getItem(key)) window.localStorage.setItem(key, raw);
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith(`atl_bak_${tid}_`))
        .sort().slice(0, -10)
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (e) { /* quota plein : on n'empêche pas l'app de tourner */ }
  }, [tid]);
  useEffect(() => {
    let alive = true;
    if (!tid) return () => {};
    getPlRevenueView(tid, model.currentYear || y0).then((res) => {
      if (!alive) return;
      const view = res?.error ? null : res?.data;
      if (!view || !Array.isArray(view.rows)) { setRevLines([]); setFin(null); return; }
      // Lignes de revenus = lignes détail de la section revenue (code = id de ligne).
      const seen = new Set();
      const lines = []; const real = {}; const months = {};
      view.rows.filter((r) => r.type === 'detail' && r.section === 'revenue').forEach((r) => {
        if (seen.has(r.label)) return; seen.add(r.label);
        lines.push({ id: r.code, label: r.label });
        real[r.code] = (Number(r.total_ytd) || 0) / 1000; // réalisé = YTD (mois clos), kCHF
        months[r.code] = Array.from({ length: 12 }, (_, i) => (Number(r.months?.[String(i + 1)]) || 0) / 1000);
      });
      const closed = Number(view.closed_count) || 0;
      // L'exercice du plan est celui choisi en haut de page (N) : le document
      // le suit, et la fenêtre montre N ou N · N+1 (décision 2026-09-04, après
      // deux règles fondées sur la clôture qui emportaient l'année trop loin).
      if ((model.currentYear || y0) !== y0) { update((d) => { d.currentYear = y0; d.closedMonths = 0; }); return; }
      setRevLines(lines);
      // Compta disponible seulement si des mois sont clos (sinon tenant sans Finance / non démarré).
      setFin(closed > 0 ? { real, months, closed } : null);
      if ((model.closedMonths || 0) !== closed) update((d) => { d.closedMonths = closed; });
    });
    // Année N−1 (réel compta) : colonne « 2025 » de la vue Plan, seulement si données.
    getPlRevenueView(tid, (model.currentYear || y0) - 1).then((res) => {
      if (!alive) return;
      const view = res?.error ? null : res?.data;
      if (!view || !Array.isArray(view.rows)) { setPrevFin(null); return; }
      const real = {};
      let has = false;
      view.rows.filter((r) => r.type === 'detail' && r.section === 'revenue').forEach((r) => {
        const v = (Number(r.total) || 0) / 1000;
        // 1re occurrence par libellé (mêmes règles de dédoublonnage que N).
        if (real[r.code] === undefined) real[r.code] = v;
        if (v) has = true;
      });
      setPrevFin(has ? { real } : null);
    });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid, model.currentYear, y0]);

  /* Lignes de charges directes du compte de résultat, pour SAVOIR où atterrit
     le coût matériel des cards (2026-08-16) : le bloc du bas propose de le
     poser sur une ligne (« Coût des produits » / « Coût des prestations »).
     Sans ce choix, les cards calculent dans le vide — c'est exactement ce
     qu'Olivier constatait : coûts visibles en bas, absents de la card. */
  useEffect(() => {
    let alive = true;
    // Au changement de client, la liste de l'ancien ne vaut plus rien : sans ce
    // vide, l'allocation automatique repartait avec ses identifiants et le
    // serveur répondait « Ligne inconnue » (constat KeyIT, 2026-09-04).
    setCogsFin([]); setCogsLoaded(false);
    if (!tid) return () => {};
    getPlView(tid, model.currentYear || y0).then((res) => {
      if (!alive) return;
      const v = res?.error ? null : res?.data;
      const ls = (v?.lines || []).filter((l) => l.section === 'cogs' && !l.hidden
        && l.line_role !== 'purchases' && l.line_role !== 'stock_variation');
      setCogsFin(ls.map((l) => ({ id: l.id, label: l.label, method: l.forecast_method || '' })));
      setCogsLoaded(true);
    });
    return () => { alive = false; };
  }, [tid, model.currentYear, y0, fcStamp]);

  /* ALLOCATION PAR DÉFAUT — décision Olivier 2026-08-16 : aucun coût de card
     ne doit rester orphelin. Les deux lignes d'accueil sont OBLIGATOIRES pour
     la planification : si elles manquent, on les crée ; toute card sans
     allocation rejoint celle de sa famille (produits → « Coût des produits »,
     prestations → « Coût des prestations »). Ensuite, l'allocation se change
     card par card — on ne réécrit jamais un choix existant. */
  const autoAlloc = useRef(false);
  const autoAllocKo = useRef(null);   // tenant où la création a échoué : on ne boucle pas
  useEffect(() => {
    // `syncedFor` : le plan affiché est bien celui de CE client (au changement
    // de client, le modèle de l'ancien reste en mémoire le temps du chargement).
    if (!tid || !canWrite || !cogsLoaded || syncedFor.current !== tid || autoAlloc.current || autoAllocKo.current === tid) return;
    const prodCards = productPlanOf(model).filter((c) => c.kind !== 'trade');
    const svcCards = servicePlanOf(model);
    if (!prodCards.length && !svcCards.length) return;
    const alloc = model.cardCostLines || {};
    const orphanProd = prodCards.filter((c) => !alloc[c.id]);
    const orphanSvc = svcCards.filter((c) => !alloc[c.id]);
    if (!orphanProd.length && !orphanSvc.length) return;
    autoAlloc.current = true;
    (async () => {
      // Ligne d'accueil de la famille : celle déjà utilisée par une card de la
      // même famille, sinon celle qui porte le libellé, sinon on la crée.
      const ensure = async (cards, label, goods) => {
        const used = cards.map((c) => alloc[c.id]).find((v) => v && cogsFin.some((l) => l.id === v));
        if (used) return used;
        const named = cogsFin.find((l) => l.label === label);
        if (named) return named.id;
        const r = await createLine(tid, 'cogs', label);
        const id = r?.error ? null : (r?.data?.id || null);
        if (id && goods) await updateLine(tid, id, { stock_linked: true });
        return id;
      };
      const prodLine = orphanProd.length
        ? await ensure(prodCards, t('scenarii:atterrissage.lineProducts', 'Coût des produits'), true) : null;
      const svcLine = orphanSvc.length
        ? await ensure(svcCards, t('scenarii:atterrissage.lineServices', 'Coût des prestations'), false) : null;
      // Ligne d'accueil impossible à obtenir : on s'arrête là pour ce client
      // (pas de tentative en boucle), les cards restent visiblement orphelines.
      if ((orphanProd.length && !prodLine) || (orphanSvc.length && !svcLine)) {
        autoAllocKo.current = tid; return;
      }
      const next = deepMutable(model);
      if (!next.cardCostLines) next.cardCostLines = {};
      if (prodLine) orphanProd.forEach((c) => { next.cardCostLines[c.id] = prodLine; });
      if (svcLine) orphanSvc.forEach((c) => { next.cardCostLines[c.id] = svcLine; });
      setModel(next);
      await putSalesPlanSection(tid, next, PLAN_OWNED,
        (merged) => ((revLines || []).length
          ? joinedPlanByLine(merged, revLines.map((l) => l.id))
          : merged.computed)).catch(() => {});
      for (const id of [prodLine, svcLine].filter(Boolean)) {
        await updateLine(tid, id, { forecast_method: 'sales_cards' });
        await generateForecast(tid, { year: model.currentYear, line_id: id });
      }
      setFcStamp((s2) => s2 + 1);
    })().catch(() => {}).finally(() => { autoAlloc.current = false; });
    // eslint-disable-next-line
  }, [tid, canWrite, cogsLoaded, cogsFin, model.cardCostLines, model.productPlan, model.servicePlan]);

  const update = (fn) => { const next = deepMutable(model); fn(next); setModel(next); };

  const cur = model.currentYear;
  const years = yearsOf(model);
  const focusYear = cur;                       // la vue mensuelle part de l'année en cours
  const isMonthly = view !== 'annual';
  // Fenêtre mensuelle : l'année en cours seule, ou avec la suivante côte à côte
  // (même bouton Période que la Synthèse des effectifs, 2026-09-04). Seule
  // l'année en cours a des mois clos.
  const yearsShown = isMonthly ? (win === '2' ? [cur, cur + 1] : [cur]) : [];
  const closedY = (y) => (isMonthly && y === cur ? closedOf(model, cur) : 0);
  const closed = closedY(cur);
  const showReal = !isMonthly || closed > 0;
  const showPrev = !isMonthly && !!prevFin; // colonne N−1 (réel compta) si données
  const prevReal = (id) => Number(prevFin?.real?.[id] || 0);
  const scale = unit === 'chf' ? 1000 : 1;
  const lines = revLines || [];
  const drivers = allDrivers(model);
  const variables = variablesOf(model);
  const lineLabel = (id) => (lines.find((l) => l.id === id) || {}).label || '';
  // Numéro de ligne de revenus (ordre du P&L) — affiché dans le CA et utilisé
  // comme référence compacte dans les cards Inducteurs / Variables.
  const lineNo = (id) => { const i = lines.findIndex((l) => l.id === id); return i >= 0 ? i + 1 : null; };
  // Ligne d'écart compta (ex-« Alignement Finance ») : « Autres <libellé> ».
  const adjLabel = (id) => {
    const l = lineLabel(id);
    return l ? `${t('scenarii:atterrissage.adjustPrefix', 'Autres')} ${l.charAt(0).toLowerCase()}${l.slice(1)}` : t('scenarii:atterrissage.adjust', 'Alignement Finance');
  };
  const isOpen = (id, hasD) => (open[id] === undefined ? hasD : open[id]);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !isOpen(id, detailsOf(model, id).length > 0) }));
  const mut = (ref, fn) => update((d) => fn(d, ref.did ? detailEntry(d, ref.lineId, ref.did) : lineEntry(d, ref.lineId)));

  // Overlay COMPTA (Finance) : réalisé + mois clos de la LIGNE = compta (lecture seule)
  // quand connecté ; le vendeur ne saisit que le prévu (détails), l'ajustement boucle.
  const curClosed = fin ? fin.closed : (model.closedMonths || 0);
  const finReal = (id) => (fin ? (Number(fin.real[id]) || 0) : 0);
  const finMonth = (id, m) => (fin && fin.months[id] ? (Number(fin.months[id][m]) || 0) : 0);
  const detsRealized = (id) => detailsOf(model, id).reduce((s, dd) => s + detailRealized(model, dd), 0);
  const detsMonth = (id, y, m) => detailsOf(model, id).reduce((s, dd) => s + detailMonth(model, dd, y, m), 0);
  // Total de ligne SANS variables (base d'un %, évite la récursion) : compta réel
  // + prévu manuel/inducteurs.
  const LAnv = (id, y) => {
    // Total de ligne SANS variables = compta/détails + plan « Autres … » (adjPlan)
    // + CA du Plan produits. Ligne POSSÉDÉE par un plan : le plan pilote SEUL
    // (les anciennes saisies sont écartées, sinon double compte).
    if (fin && y === cur) { let f = 0; for (let m = curClosed; m < 12; m++) f += planBaseM(id, cur, m); return finReal(id) + f; }
    if (prodLines.has(id)) return prodA(id, y);
    return lineAnnual(model, id, y) + adjPlanA(id, y) + prodA(id, y);
  };
  // Base d'un variable : ligne entière, détail précis, ou Alignement Finance.
  const baseAnnual = (ref, y) => {
    const r = parseRef(ref);
    if (r.kind === 'det') { const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did); return dd ? detAnnualFull(dd, y) : 0; }
    if (r.kind === 'adj') return y === cur ? adjRealized(r.lineId) : 0;
    return LAnv(r.lineId, y);
  };
  const detAnnualFull = (dd, y) => detailAnnual(model, dd, y); // alias lisible
  const baseReal = (ref) => {
    const r = parseRef(ref);
    if (!fin) return 0;
    if (r.kind === 'det') { const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did); return dd ? detailRealized(model, dd) : 0; }
    if (r.kind === 'adj') return adjRealized(r.lineId);
    return finReal(r.lineId);
  };
  // Base MENSUELLE (sans variables) : le % s'applique mois par mois — jamais
  // l'annuel étalé (sinon juillet hérite d'1/12 du réel de janvier-juin).
  const baseMonthNv = (ref, y, m) => {
    const r = parseRef(ref);
    if (r.kind === 'det') { const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did); return dd ? detailMonth(model, dd, y, m) : 0; }
    if (r.kind === 'adj') return (y === cur && m < curClosed) ? adjMonth(r.lineId, m) : 0;
    if (fin && y === cur && m < curClosed) return finMonth(r.lineId, m);
    return planBaseM(r.lineId, y, m);
  };
  const varAnnualP = (v, y) => heldVarRate(v, y, cur) / 100 * baseAnnual(v.baseRef, y);
  const varMonthP = (v, y, m) => heldVarRate(v, y, cur) / 100 * baseMonthNv(v.baseRef, y, m);
  const varsFor = (id) => variablesOf(model).filter((v) => parseRef(v.targetRef).lineId === id);
  const varForecastFor = (id, y) => varsFor(id).reduce((s, v) => s + varAnnualP(v, y), 0);
  const varForecastMonth = (id, y, m) => varsFor(id).reduce((s, v) => s + varMonthP(v, y, m), 0);

  // Plan manuel « Autres <ligne> » (futur uniquement — le réel reste la compta).
  const adjPlanM = (id, y, m) => Number(monthsOf(adjPlanOf(model, id), y)[m]) || 0;
  const adjPlanA = (id, y) => annualOf(model, adjPlanOf(model, id), y);

  // Lignes POSSÉDÉES par le Plan produits : leur CA prévisionnel vient des
  // cards (volumes × prix) — elles se LISENT ici, se planifient là-bas. Le
  // plan pilote SEUL : saisies, variables et plan « Autres … » de la ligne
  // sont écartés (sinon double compte à la connexion d'une card).
  const prodLines = new Set(plannedLines(model));
  const prodM = (id, y, m) => productPlanRevenueMonth(model, id, y, m) + servicePlanRevenueMonth(model, id, y, m);
  const prodA = (id, y) => { let s = 0; for (let m = 0; m < 12; m++) s += prodM(id, y, m); return s; };
  // Base PRÉVUE d'une ligne (sans variables) : plan seul si possédée.
  // Ligne pilotée par un plan (produits/services) : ses cards pilotent, MAIS le
  // plan manuel du résiduel « Autres … » s'y ajoute — c'est un complément, pas
  // un doublon (le résiduel n'a pas de compta sur les mois à venir). Sans lui,
  // une règle posée sur « Autres … » n'entrait pas dans le total de la ligne
  // (constat 2026-08-16).
  const planBaseM = (id, y, m) => (prodLines.has(id)
    ? prodM(id, y, m) + adjPlanM(id, y, m)
    : lineMonth(model, id, y, m) + adjPlanM(id, y, m) + prodM(id, y, m));

  const LR = (id) => (fin ? finReal(id) : lineRealized(model, id));
  const LM = (id, y, m) => ((fin && y === cur && m < curClosed)
    ? finMonth(id, m)
    : planBaseM(id, y, m) + (prodLines.has(id) ? 0 : varForecastMonth(id, y, m)));
  const LA = (id, y) => {
    if (y === cur || y === cur + 1) {
      // Années mensuelles : total = réel (compta si connecté) + Σ mois ouverts.
      let s = 0;
      for (let m = 0; m < 12; m++) s += LM(id, y, m);
      if (fin && y === cur) { s = finReal(id); for (let m = curClosed; m < 12; m++) s += LM(id, y, m); }
      return s;
    }
    if (prodLines.has(id)) return prodA(id, y);
    return lineAnnual(model, id, y) + varForecastFor(id, y) + adjPlanA(id, y) + prodA(id, y);
  };
  // « Autres <ligne> » = RÉSIDUEL du réel : compta − détails − contributions
  // des plans (Contrats clés, cards produits… saisis aussi sur les mois clos).
  // Sans la déduction des plans, le « Autres » répétait toute la compta et la
  // décomposition sommait au-dessus du réel (bug du 2026-08-13).
  const prodRealized = (id) => { let s = 0; for (let m = 0; m < curClosed; m++) s += prodM(id, cur, m); return s; };
  const adjRealized = (id) => finReal(id) - detsRealized(id) - prodRealized(id);
  const adjMonth = (id, m) => finMonth(id, m) - detsMonth(id, cur, m) - prodM(id, cur, m);

  /* Mois RÉELS (clos) qui servent de base aux règles de projection d'une ligne
     saisie à la main : « Autres <ligne> » (résiduel) et détails manuels. */
  const adjRealVals = (id) => MONTHS.map((_, i) => (i < closed && focusYear === cur ? adjMonth(id, i) : null)).filter((x) => x != null);
  const detailRealVals = (dd) => MONTHS.map((_, i) => (i < closed && focusYear === cur ? detailMonth(model, dd, cur, i) : null)).filter((x) => x != null);

  /* RECALCUL AUTOMATIQUE DES RÈGLES (2026-08-19) — un mois de compta de plus
     arrive : toute ligne dont la règle est armée se reprojette seule. Sans ça,
     « Moyenne YTD » restait figée sur la moyenne du jour où on l'avait cliquée
     (le chip proposait 197, les cellules gardaient 192). On n'écrit QUE si le
     résultat diffère — sinon la sauvegarde différée tournerait en boucle. */
  useEffect(() => {
    if (!canWrite || !fin || curClosed <= 0) return;
    const todo = [];
    const check = (entryNow, entryIn, vals) => {
      const rid = entryRule(entryNow);
      if (!rid) return;
      const v = ruleValue(rid, vals);
      const months = monthsOf(entryNow, cur);
      for (let m = curClosed; m < 12; m++) {
        if (Math.abs((Number(months[m]) || 0) - v) > 1e-9) { todo.push({ entryIn, vals, rid }); return; }
      }
    };
    (revLines || []).forEach((l) => {
      check(adjPlanOf(model, l.id), (d) => adjEntry(d, l.id),
        MONTHS.map((_, i) => (i < curClosed ? adjMonth(l.id, i) : null)).filter((x) => x != null));
      detailsOf(model, l.id).forEach((dd) => {
        if (dd.byDriver) return;
        check(dd, (d) => detailEntry(d, l.id, dd.id),
          MONTHS.map((_, i) => (i < curClosed ? detailMonth(model, dd, cur, i) : null)).filter((x) => x != null));
      });
    });
    if (!todo.length) return;
    update((d) => todo.forEach((x) => applyEntryRule(d, x.entryIn(d), cur, curClosed, x.vals, x.rid)));
    // eslint-disable-next-line
  }, [model, fin, revLines, canWrite]);

  // Modale d'édition d'inducteur (driver + prix). La ligne est fixe (détail rattaché).
  const openEditDrv = (lineId, dd) => setDrvModal({ lineId, did: dd.id, dmode: dd.dmode || 'unit', form: { name: dd.name || '', driver: dd.driver || '', baseLineId: dd.baseLineId || '', prix: dd.prix || 0 } });
  const setForm = (k, v) => setDrvModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));
  const saveDrv = () => {
    const { lineId, did, dmode, form } = drvModal;
    update((d) => {
      setDetailField(d, lineId, did, 'name', form.name);
      setDetailField(d, lineId, did, 'prix', Math.round(Number(form.prix) || 0));
      if (dmode === 'pct') setDetailField(d, lineId, did, 'baseLineId', form.baseLineId);
      else setDetailField(d, lineId, did, 'driver', form.driver);
    });
    setDrvModal(null);
  };
  // Choix de l'inducteur depuis le menu « + inducteur » (unité, % de ligne, ou libre).
  const pickUnit = (lineId, dd, key) => {
    setIndMenu(null);
    if (key === '__other') { update((d) => attachDriver(d, lineId, dd.id, '')); setDrvModal({ lineId, did: dd.id, dmode: 'unit', form: { name: dd.name || '', driver: '', prix: 1400 } }); return; }
    update((d) => attachDriver(d, lineId, dd.id, key));
  };

  // Revenus variables (card Variables) : cible × base × taux/an ; taux réel observé.
  // Libellé d'une référence de base (ligne / détail / Alignement Finance).
  const refLabel = (ref) => {
    const r = parseRef(ref);
    if (r.kind === 'det') { const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did); return `${dd?.name || t('scenarii:atterrissage.detailPh', 'Détail')} (${lineLabel(r.lineId)})`; }
    if (r.kind === 'adj') return adjLabel(r.lineId);
    return lineLabel(r.lineId);
  };
  // Réalisé de la cible (ligne entière ou détail précis).
  const targetReal = (ref) => {
    const r = parseRef(ref);
    if (r.kind === 'det') { const dd = detailsOf(model, r.lineId).find((x) => x.id === r.did); return dd ? detailRealized(model, dd) : 0; }
    return Number(fin?.real?.[r.lineId] || 0);
  };
  // Taux réel = cible réel ÷ base réelle (annuel + par mois clos).
  const realRate = (v) => { const b = baseReal(v.baseRef); const tr = targetReal(v.targetRef); return (fin && b) ? Math.round(tr / b * 1000) / 10 : null; };
  const realRateMonth = (v, m) => {
    const rb = parseRef(v.baseRef);
    const b = rb.kind === 'line' && fin && fin.months[rb.lineId] ? Number(fin.months[rb.lineId][m])
      : (rb.kind === 'adj' ? adjMonth(rb.lineId, m) : null);
    const tg = parseRef(v.targetRef);
    const tv = tg.kind === 'adj' ? adjMonth(tg.lineId, m) : Number((fin?.months?.[tg.lineId] || [])[m] || 0);
    return b ? Math.round(tv / b * 1000) / 10 : null;
  };
  // Taux réel de l'ANNÉE PRÉCÉDENTE (compta N-1). Une cible/base « Autres … »
  // (adj) se lit sur la LIGNE ENTIÈRE : en N-1 il n'y a pas de détails de plan,
  // le « Autres » d'alors EST la ligne. Seuls les détails (det) restent sans N-1.
  const realRatePrev = (v) => {
    const rb = parseRef(v.baseRef);
    const tg = parseRef(v.targetRef);
    if (rb.kind === 'det' || tg.kind === 'det') return null;
    const b = prevReal(rb.lineId);
    return b ? Math.round(prevReal(tg.lineId) / b * 1000) / 10 : null;
  };
  const openNewVar = () => { const target = lines[0]?.id || ''; const base = (lines.find((l) => l.id !== target) || {}).id || ''; setVarModal({ id: null, form: { name: '', targetRef: target ? `line:${target}` : '', baseRef: base ? `line:${base}` : '', rates: {} } }); };
  const openEditVar = (v) => setVarModal({ id: v.id, form: { name: v.name || '', targetRef: v.targetRef || '', baseRef: v.baseRef || '', rates: { ...(v.rate || {}) } } });
  const setVarForm = (k, val) => setVarModal((m) => ({ ...m, form: { ...m.form, [k]: val } }));
  const saveVar = () => {
    const { id, form } = varModal;
    const rates = {};
    Object.entries(form.rates || {}).forEach(([y, val]) => {
      if (val !== '' && val != null) rates[y] = Number(val) || 0;
    });
    if (!id) {
      update((d) => {
        addVariable(d, form.targetRef, form.baseRef);
        const last = d.variables[d.variables.length - 1];
        if (last) { last.name = form.name; last.rate = rates; }
      });
    } else {
      update((d) => {
        setVariableField(d, id, 'name', form.name);
        setVariableField(d, id, 'targetRef', form.targetRef);
        setVariableField(d, id, 'baseRef', form.baseRef);
        setVariableField(d, id, 'rate', rates);
      });
    }
    setVarModal(null);
  };

  // Ordre mensuel : mois réels · Réalisé · mois prévisionnels · Total to go · année.
  // Poignée de redimensionnement de la colonne libellé (drag, min 140 / max 480).
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX; const startW = nameW;
    const move = (ev) => setNameW(Math.max(140, Math.min(480, startW + ev.clientX - startX)));
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };
  const NameTh = ({ children }) => (
    <>
      <th className="l atl-nameth">{children}<span className="atl-resizer" onMouseDown={startResize} aria-hidden /></th>
      <th />
    </>
  );

  // Largeur totale EXPLICITE de la table (sinon table-layout:fixed dérive et les
  // colonnes des 3 cards ne s'alignent plus au pixel).
  // En CHF, les nombres sont 3 chiffres plus longs : les colonnes s'élargissent
  // (sinon ils se chevauchent — constat 2026-08-16).
  const wide = unit === 'chf';
  const COLW = wide
    ? { mo: 78, real: 96, yr: 120, tot: 104, del: 22, rule: 132 }
    : { mo: 54, real: 68, yr: 100, tot: 74, del: 22, rule: 132 };
  // + COLW.rule : colonne 2 réservée (vide ici, « Règle » dans Coûts directs)
  // pour que les deux cartes s'alignent au pixel (2026-08-16).
  const yearCols = (y) => 12 + (closedY(y) > 0 ? 2 : 0) + 1;   // mois · réalisé · à venir · total
  const tableW = nameW + COLW.rule + COLW.del + (isMonthly
    ? yearsShown.reduce((s0, y) => s0 + 12 * COLW.mo + (closedY(y) > 0 ? COLW.real + COLW.tot : 0) + COLW.tot, 0)
    : (showPrev ? COLW.real : 0) + (showReal ? COLW.real : 0) + years.length * COLW.yr);

  const ColGroup = () => (
    <colgroup>
      <col className="atl-col-name" style={{ width: nameW }} />
      <col style={{ width: COLW.rule }} />
      {isMonthly ? yearsShown.map((y) => {
        const cy = closedY(y);
        return (
          <React.Fragment key={y}>
            {MONTHS.slice(0, cy).map((_, i) => <col key={`r${i}`} style={{ width: COLW.mo }} />)}
            {cy > 0 && <col style={{ width: COLW.real }} />}
            {MONTHS.slice(cy).map((_, i) => <col key={`f${i}`} style={{ width: COLW.mo }} />)}
            {cy > 0 && <col style={{ width: COLW.tot }} />}
            <col style={{ width: COLW.tot }} />
          </React.Fragment>
        );
      }) : (
        <>
          {showPrev && <col style={{ width: COLW.real }} />}
          {showReal && <col style={{ width: COLW.real }} />}
          {years.map((y) => <col key={y} style={{ width: COLW.yr }} />)}
        </>
      )}
      <col className="atl-col-del" />
    </colgroup>
  );
  const ValHead = () => (
    <>
      {isMonthly ? yearsShown.map((y) => {
        const cy = closedY(y);
        return (
          <React.Fragment key={y}>
            {MONTHS.slice(0, cy).map((mo, i) => <th key={`r${i}`}>{mo}</th>)}
            {cy > 0 && <th className="atl-filet-l atl-filet-r">{t('scenarii:atterrissage.realized', 'Réalisé')}<br /><span className="atl-sub">{t('scenarii:atterrissage.nMonths', '{{n}} mois', { n: cy })}</span></th>}
            {MONTHS.slice(cy).map((mo, i) => <th key={`f${i}`}>{mo}</th>)}
            {cy > 0 && <th className="atl-filet-l">{t('scenarii:atterrissage.toGo', 'À venir')}<br /><span className="atl-sub">{t('scenarii:atterrissage.nMonths', '{{n}} mois', { n: 12 - cy })}</span></th>}
            <th className="atl-filet-l">{t('scenarii:atterrissage.totalYear', 'Total {{year}}', { year: y })}</th>
          </React.Fragment>
        );
      }) : (
        <>
          {showPrev && <th className="atl-filet-r">{cur - 1}<br /><span className="atl-sub">{t('scenarii:atterrissage.subReal', 'réel')}</span></th>}
          {showReal && <th className="atl-filet-r">{cur}<br /><span className="atl-sub">{t('scenarii:atterrissage.realized', 'Réalisé')}</span></th>}
          {years.map((yy) => (
            <th key={yy}>{yy}<br /><span className="atl-sub">{yy === cur ? t('scenarii:atterrissage.subForecast', 'prév.') : t('scenarii:atterrissage.subPlan', 'plan')}</span></th>
          ))}
        </>
      )}
      <th />
    </>
  );
  const GroupHead = () => (isMonthly ? (
    <tr className="atl-headgroup">
      <th className="l" />
      <th />
      {yearsShown.map((y) => {
        const cy = closedY(y);
        return (
          <React.Fragment key={y}>
            {cy > 0 && <th colSpan={cy} className="atl-grp">{t('scenarii:atterrissage.real', 'réel')}</th>}
            {cy > 0 && <th className="atl-filet-l atl-filet-r" />}
            <th colSpan={12 - cy} className="atl-grp">{yearsShown.length > 1 ? `${t('scenarii:atterrissage.forecast', 'prévisionnel')} ${y}` : t('scenarii:atterrissage.forecast', 'prévisionnel')}</th>
            {cy > 0 && <th className="atl-filet-l" />}
            <th className="atl-filet-l" />
          </React.Fragment>
        );
      })}
      <th />
    </tr>
  ) : null);

  const rCell = (v, cls) => <td className={`atl-num${cls ? ' ' + cls : ''}`}>{fmt((Number(v) || 0) * scale)}</td>;
  const eCell = (v, onCommit, cls, muted, mult = scale, onPasteVals) => <td className={cls}><Num value={v} mult={mult} muted={muted} onCommit={onCommit} onPasteVals={onPasteVals} /></td>;
  const addSpan = isMonthly ? 3 + yearsShown.reduce((s0, y) => s0 + yearCols(y), 0) : 1 + years.length + 3 + (showPrev ? 1 : 0);
  const nameCell = (label, btn, extraCls = '', rule = null) => (
    <>
      <td className={`l ${extraCls}`}><span className="atl-tgl">{btn}</span><span className="atl-nm">{label}</span></td>
      <td className="atl-rulecell">{rule}</td>
    </>
  );

  /* RÈGLE DE PROJECTION — proposée UNIQUEMENT sur les lignes qu'on saisit à la
     main : « Autres <ligne> » (résiduel) et détails ajoutés. Les lignes tenues
     par le Plan produits / Plan services n'en ont pas : leur futur vient de
     leur propre moteur (décision 2026-08-16).
     La règle ÉCRIT les mois ouverts — ils restent modifiables ensuite. */
  const RULES = [
    { id: 'last', label: t('scenarii:atterrissage.rLast', 'Dernier mois') },
    { id: 'avg', label: t('scenarii:atterrissage.rAvg', 'Moyenne YTD') },
    { id: 'zero', label: t('scenarii:atterrissage.rZero', 'Vide (0)') },
  ];
  const RuleChip = ({ realVals, write, current }) => {
    const [open, setOpen] = useState(false);
    const n = realVals.length;
    const valOf = (id) => ruleValue(id, realVals);
    if (!canWrite || !isMonthly) return null;
    return (
      <span className="atl-rulewrap">
        <button type="button" className={`atl-rulebtn${current ? ' is-set' : ''}`}
          title={t('scenarii:atterrissage.ruleHint', 'Règle de projection des mois à venir')}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
          {(RULES.find((r) => r.id === current) || {}).label || t('scenarii:atterrissage.ruleShort', 'règle')} <i aria-hidden>▾</i>
        </button>
        {open && (
          <span className="atl-rulepop" onMouseLeave={() => setOpen(false)}>
            {RULES.map((r) => (
              <button key={r.id} type="button" className="atl-rulepop__i"
                disabled={r.id !== 'zero' && !n}
                onClick={(e) => {
                  e.stopPropagation();
                  write(r.id);
                  setOpen(false);
                }}>
                <span>{r.label}</span>
                <em>{fmt(valOf(r.id) * scale)}</em>
              </button>
            ))}
          </span>
        )}
      </span>
    );
  };

  // Cellules d'une ENTRÉE manuelle (détail direct ou ligne directe) — éditable / lecture seule.
  const caCells = (opts) => {
    const { entry, ref, ro, valRealized, valYear, valMonth } = opts;
    if (isMonthly) {
      // Par année affichée : mois réels · Réalisé · mois prévisionnels · À venir · total.
      const bloc = (y) => {
        const cy = closedY(y);
        const mv = (i) => (ro ? valMonth(y, i) : (Number(monthsOf(entry, y)[i]) || 0));
        const mcell = (i) => (ro
          ? <React.Fragment key={i}>{rCell(valMonth(y, i))}</React.Fragment>
          : <React.Fragment key={i}>{eCell(monthsOf(entry, y)[i], (vv) => mut(ref, (d, e) => setMonth(d, e, y, i, vv)), '', i < cy, scale,
            (vals) => mut(ref, (d, e) => vals.forEach((val, k) => { if (i + k < 12) setMonth(d, e, y, i + k, val); })))}</React.Fragment>);
        const toGo = MONTHS.reduce((s0, _, i) => (i >= cy ? s0 + mv(i) : s0), 0);
        return (
          <React.Fragment key={y}>
            {MONTHS.map((_, i) => (i < cy ? mcell(i) : null))}
            {cy > 0 && (ro ? rCell(valRealized, 'atl-filet-l atl-filet-r') : eCell(realizedOf(model, entry), (v) => mut(ref, (d, e) => setRealized(d, e, v)), 'atl-filet-l atl-filet-r'))}
            {MONTHS.map((_, i) => (i >= cy ? mcell(i) : null))}
            {cy > 0 && rCell(toGo, 'atl-filet-l')}
            {rCell(ro ? valYear(y) : annualOf(model, entry, y), 'atl-filet-l')}
          </React.Fragment>
        );
      };
      return <>{yearsShown.map(bloc)}</>;
    }
    return (
      <>
        {ro ? rCell(valRealized, 'atl-filet-r') : eCell(realizedOf(model, entry), (v) => mut(ref, (d, e) => setRealized(d, e, v)), 'atl-filet-r')}
        {years.map((yy, yi) => (ro
          ? <React.Fragment key={yy}>{rCell(valYear(yy))}</React.Fragment>
          : <React.Fragment key={yy}>{eCell(annualOf(model, entry, yy), (v) => mut(ref, (d, e) => setAnnual(d, e, yy, v)), '', false, scale,
            (vals) => mut(ref, (d, e) => vals.forEach((val, k) => { const y2 = years[yi + k]; if (y2 != null) setAnnual(d, e, y2, val); })))}</React.Fragment>))}
      </>
    );
  };

  // Détail À INDUCTEUR dans le CA : revenu éditable en annuel (→ recalcule la qté), réparti lecture seule en mensuel.
  const driverCaCells = (lineId, dd) => {
    const pct = isPctDetail(dd);
    if (isMonthly) {
      const bloc = (y) => {
        const cy = closedY(y);
        const mv = (i) => detailMonth(model, dd, y, i);
        const toGo = MONTHS.reduce((s0, _, i) => (i >= cy ? s0 + mv(i) : s0), 0);
        const editableReal = !pct && y === cur; // CA réel saisi → recalcule les jours-hommes
        // Collage Excel : remplit les mois suivants (réel → setDriverRealMonth, prévu → setDriverPlanMonth).
        const pasteDrvMonths = (start) => (vals) => update((d) => vals.forEach((val, k) => {
          const mm = start + k; if (mm >= 12) return;
          if (y === cur && mm < cy) setDriverRealMonth(d, lineId, dd.id, mm, val);
          else setDriverPlanMonth(d, lineId, dd.id, y, mm, qtyFromCa(dd, val));
        }));
        return (
          <React.Fragment key={y}>
            {MONTHS.map((_, i) => (i < cy ? (
              editableReal
                ? <React.Fragment key={i}>{eCell(mv(i), (v) => update((d) => setDriverRealMonth(d, lineId, dd.id, i, v)), '', false, scale, pasteDrvMonths(i))}</React.Fragment>
                : <React.Fragment key={i}>{rCell(mv(i))}</React.Fragment>
            ) : null))}
            {cy > 0 && (editableReal
              ? eCell(detailRealized(model, dd), (v) => update((d) => setDriverRealTotal(d, lineId, dd.id, v)), 'atl-filet-l atl-filet-r')
              : rCell(detailRealized(model, dd), 'atl-filet-l atl-filet-r'))}
            {MONTHS.map((_, i) => (i >= cy ? (
              pct
                ? <React.Fragment key={i}>{rCell(mv(i))}</React.Fragment>
                : <React.Fragment key={i}>{eCell(mv(i), (v) => update((d) => setDriverPlanMonth(d, lineId, dd.id, y, i, qtyFromCa(dd, v))), '', false, scale, pasteDrvMonths(i))}</React.Fragment>
            ) : null))}
            {cy > 0 && rCell(toGo, 'atl-filet-l')}
            {rCell(detailAnnual(model, dd, y), 'atl-filet-l')}
          </React.Fragment>
        );
      };
      return <>{yearsShown.map(bloc)}</>;
    }
    return (
      <>
        {showReal && (pct
          ? rCell(detailRealized(model, dd), 'atl-filet-r')
          : eCell(detailRealized(model, dd), (v) => update((d) => setDriverRealTotal(d, lineId, dd.id, v)), 'atl-filet-r'))}
        {years.map((yy, yi) => <React.Fragment key={yy}>{pct
          ? rCell(detailAnnual(model, dd, yy))
          : eCell(detailAnnual(model, dd, yy), (v) => update((d) => setDetailQty(d, lineId, dd.id, yy, qtyFromCa(dd, v))), '', false, scale,
            (vals) => update((d) => vals.forEach((val, k) => { const y2 = years[yi + k]; if (y2 != null) setDetailQty(d, lineId, dd.id, y2, qtyFromCa(dd, val)); })))}</React.Fragment>)}
      </>
    );
  };

  const renderLine = (line) => {
    const details = detailsOf(model, line.id);
    // Ligne POSSÉDÉE par le Plan produits : son prévisionnel vient des
    // catégories (volumes × prix) — lecture seule ici, et le détail s'ouvre par
    // défaut pour qu'on VOIE la contribution de chaque catégorie.
    const owned = prodLines.has(line.id);
    const opened = isOpen(line.id, details.length > 0 || owned);
    const rows = [];
    const btn = <button className={`atl-tgl-btn${opened ? ' is-open' : ''}`} onClick={() => toggle(line.id)} aria-label={opened ? 'Réduire' : 'Développer'}>{opened ? '−' : '+'}</button>;
    // Ligne agrégée (lecture seule) si connectée à la compta, détaillée, ou
    // possédée par le Plan produits ; sinon saisie directe.
    const roLine = !!fin || details.length > 0 || owned;
    // Ligne CIBLÉE par une règle (revenu variable) : teinte + pastille ƒ% qui
    // OUVRE la règle — la card Variables n'est plus le seul point d'entrée.
    const allRules = varsFor(line.id);
    const rules = allRules.filter((v) => parseRef(v.targetRef).kind !== 'adj' || !opened);
    rows.push(
      <tr key={line.id} className={`atl-line${rules.length ? ' atl-line--ruled' : ''}`}>
        {/* Ligne de groupement : PAS de tag de source (2026-08-20) — les
            détails juste en dessous le portent déjà, dans la colonne source. */}
        {nameCell(<><span className="atl-linename">{line.label}</span>
          {/* Ligne pilotée par une RÈGLE de Finance : signalée DISCRÈTEMENT
              (un seul repère par ligne — le sélecteur de Finance porte le nom
              de la source, décision 2026-08-16). */}
          {finRuled.has(line.id) && (
            <span className="atl-finmark" title={t('scenarii:atterrissage.finRuledHint', 'Prévision produite par une règle du compte de résultat (Finance)')} aria-hidden>ƒ</span>
          )}{rules.map((v) => (
          <button key={v.id} className="atl-rulechip"
            title={`${t('scenarii:atterrissage.rule', 'Règle')} : ${v.name || '%'} · ${heldVarRate(v, focusYear, cur)} %`}
            onClick={(e) => { e.stopPropagation(); openEditVar(v); }}>ƒ%</button>
        ))}{details.length > 0 && <span className="atl-count">{details.length}</span>}</>, btn)}
        {showPrev && <td className="atl-filet-r atl-num">{fmt(prevReal(line.id) * scale)}</td>}
        {roLine
          ? caCells({ ro: true, valRealized: LR(line.id), valYear: (yy) => LA(line.id, yy), valMonth: (y, i) => LM(line.id, y, i) })
          : caCells({ entry: entryOf(model, line.id), ref: { lineId: line.id } })}
        <td className="atl-del" />
      </tr>,
    );
    if (opened) {
      if (fin) {
        // « Autres <ligne> » : réel = écart compta (verrouillé) ; FUTUR = plan manuel
        // (adjPlan) + contribution des variables ciblant cette ligne (mois par mois).
        const adjVars = variables.filter((v) => v.targetRef === `adj:${line.id}`);
        const avM = (y, m) => adjVars.reduce((s, v) => s + varMonthP(v, y, m), 0);
        const avA = (y) => (y === cur || y === cur + 1)
          ? Array.from({ length: 12 }, (_, m) => (y === cur && m < curClosed ? 0 : avM(y, m))).reduce((s, x) => s + x, 0)
          : adjVars.reduce((s, v) => s + varAnnualP(v, y), 0);
        const editable = adjVars.length === 0; // piloté par variable → lecture seule
        const valM = (y, m) => avM(y, m) + adjPlanM(line.id, y, m);
        const valY = (yy) => (yy === cur ? adjRealized(line.id) + Array.from({ length: 12 }, (_, m) => (m < curClosed ? 0 : valM(cur, m))).reduce((s, x) => s + x, 0) : avA(yy) + adjPlanA(line.id, yy));
        const mutAdj = (fn) => update((d) => fn(d, adjEntry(d, line.id)));
        rows.push(
          <tr key={`${line.id}-adj`} className="atl-detail atl-adjrow">
            {nameCell(<><span className="atl-adjname">{adjLabel(line.id)}</span>{allRules.filter((v) => parseRef(v.targetRef).kind === 'adj').map((v) => (
              <button key={v.id} className="atl-rulechip"
                title={`${t('scenarii:atterrissage.rule', 'Règle')} : ${v.name || '%'} · ${heldVarRate(v, focusYear, cur)} %`}
                onClick={(e) => { e.stopPropagation(); openEditVar(v); }}>ƒ%</button>
            ))}</>, null, 'atl-detail__name',
              editable ? (
                <RuleChip
                  current={entryRule(adjPlanOf(model, line.id))}
                  realVals={adjRealVals(line.id)}
                  write={(rid) => mutAdj((d, e) => applyEntryRule(d, e, focusYear, closed, adjRealVals(line.id), rid))}
                />
              ) : null)}
            {showPrev && <td className="atl-filet-r" />}
            {isMonthly ? yearsShown.map((y) => {
              const cy = closedY(y);
              return (
                <React.Fragment key={y}>
                  {MONTHS.map((_, i) => (i < cy ? <React.Fragment key={i}>{rCell(adjMonth(line.id, i))}</React.Fragment> : null))}
                  {cy > 0 && rCell(adjRealized(line.id), 'atl-filet-l atl-filet-r')}
                  {MONTHS.map((_, i) => (i >= cy ? (
                    editable
                      ? <React.Fragment key={i}>{eCell(adjPlanM(line.id, y, i), (v) => mutAdj((d, e) => setMonth(d, e, y, i, v)), '', false, scale,
                        (vals) => mutAdj((d, e) => vals.forEach((val, k) => { if (i + k < 12) setMonth(d, e, y, i + k, val); })))}</React.Fragment>
                      : <React.Fragment key={i}>{rCell(valM(y, i))}</React.Fragment>
                  ) : null))}
                  {cy > 0 && rCell(MONTHS.reduce((s0, _, i) => (i >= cy ? s0 + valM(y, i) : s0), 0), 'atl-filet-l')}
                  {rCell(valY(y), 'atl-filet-l')}
                </React.Fragment>
              );
            }) : (
              <>
                {showReal && rCell(adjRealized(line.id), 'atl-filet-r')}
                {years.map((yy, yi) => (editable && yy !== cur
                  ? <React.Fragment key={yy}>{eCell(adjPlanA(line.id, yy), (v) => mutAdj((d, e) => setAnnual(d, e, yy, v)), '', false, scale,
                    (vals) => mutAdj((d, e) => vals.forEach((val, k) => { const y2 = years[yi + k]; if (y2 != null && y2 !== cur) setAnnual(d, e, y2, val); })))}</React.Fragment>
                  : <React.Fragment key={yy}>{editable && yy === cur
                    // 2026 = TOTAL (réalisé + prévu) ; la saisie pilote le futur (total − réalisé).
                    ? eCell(valY(cur), (v) => mutAdj((d, e) => setAnnual(d, e, cur, v - adjRealized(line.id))), '')
                    : rCell(valY(yy))}</React.Fragment>))}
              </>
            )}
            <td className="atl-del" />
          </tr>,
        );
      }
      // Contribution du PLAN PRODUITS, en lecture : sans ces lignes, le CA des
      // catégories entrait dans le total sans laisser de trace à l'écran — on ne
      // pouvait ni le vérifier ni comprendre d'où venait l'écart.
      [...productPlanOf(model).filter((c) => c.lineId === line.id).map((c) => ({ c, svc: false })),
        ...servicePlanOf(model).filter((c) => c.lineId === line.id).map((c) => ({ c, svc: true }))].forEach(({ c, svc }) => {
        const pm = (y, i) => (svc ? serviceCardRevenueMonth(model, c, y, i) : productCardRevenueMonth(model, c, y, i));
        const pa = (yy) => Array.from({ length: 12 }, (_, i) => (svc ? serviceCardRevenueMonth(model, c, yy, i) : productCardRevenueMonth(model, c, yy, i))).reduce((s2, x) => s2 + x, 0);
        const planOpen = openPlans.has(c.id);
        rows.push(
          <tr key={`pp-${c.id}`} className="atl-detail atl-detail--planned">
            {nameCell(
              <span className="atl-detname">{c.name || t('scenarii:productPlan.category', 'Catégorie')}</span>,
              <button className={`atl-chev${planOpen ? ' is-open' : ''}`} aria-label={planOpen ? 'Réduire' : 'Développer'}
                onClick={(e) => { e.stopPropagation(); setOpenPlans((s0) => { const n = new Set(s0); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; }); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
              </button>, 'atl-detail__name',
              <span className="atl-drvtag">{svc ? t('scenarii:servicePlan.owned', 'Plan services') : t('scenarii:atterrissage.productPlanOwned', 'Plan produits')}</span>)}
            {showPrev && <td className="atl-filet-r" />}
            {caCells({ ro: true, valRealized: pa(cur), valYear: (yy) => pa(yy), valMonth: pm })}
            <td className="atl-del" />
          </tr>,
        );
        // Déplié : une sous-ligne PAR ligne de la card (client, produit…).
        if (planOpen) {
          (c.rows || []).forEach((r) => {
            const rm = (y, i) => (svc ? serviceRowRevenueMonth(model, c, r, y, i)
              : productRowQtyMonth(r, y, i) * productRowPriceAt(model, r, y) / 1000);
            const ra = (yy) => Array.from({ length: 12 }, (_, i) => (svc ? serviceRowRevenueMonth(model, c, r, yy, i)
              : productRowQtyMonth(r, yy, i) * productRowPriceAt(model, r, yy) / 1000)).reduce((s2, x) => s2 + x, 0);
            rows.push(
              <tr key={`ppr-${r.id}`} className="atl-detail atl-detail--plansub">
                {nameCell(<span className="atl-detname atl-detname--sub">{r.name || '—'}</span>, null, 'atl-detail__name')}
                {showPrev && <td className="atl-filet-r" />}
                {caCells({ ro: true, valRealized: ra(cur), valYear: (yy) => ra(yy), valMonth: rm })}
                <td className="atl-del" />
              </tr>,
            );
          });
        }
      });
      details.forEach((dd) => {
        const isDrv = !!dd.byDriver;
        const menuOpen = indMenu && indMenu.did === dd.id;
        rows.push(
          <tr key={dd.id} className={`atl-detail${isDrv ? ' atl-detail--driver' : ''}`}>
            {nameCell(
              isDrv
                ? <>{dd.name || t('scenarii:atterrissage.driverRow', 'inducteur')} <span className="atl-drvtag">{isPctDetail(dd) ? `${dd.prix || 0}% · ${lineLabel(dd.baseLineId)}` : unitLabel(dd.driver) || '—'}</span></>
                : <>
                  <Txt value={dd.name} cls="atl-detname" placeholder={t('scenarii:atterrissage.detailPh', 'Détail')} onCommit={(v) => update((d) => renameDetail(d, line.id, dd.id, v))} />
                </>,
              isDrv ? <button className="atl-pencil" onClick={() => openEditDrv(line.id, dd)} aria-label={t('scenarii:atterrissage.edit', 'Modifier')}>✎</button> : null,
              'atl-detail__name',
              // Détail SAISI à la main : il a droit à une règle de projection.
              // Un détail piloté par inducteur garde son propre moteur.
              !isDrv ? (
                <RuleChip
                  current={entryRule(dd)}
                  realVals={detailRealVals(dd)}
                  write={(rid) => update((d) => applyEntryRule(d, detailEntry(d, line.id, dd.id), focusYear, closed, detailRealVals(dd), rid))}
                />
              ) : null,
            )}
            {showPrev && <td className="atl-filet-r" />}
            {isDrv ? driverCaCells(line.id, dd) : caCells({ entry: dd, ref: { lineId: line.id, did: dd.id } })}
            <td className="atl-del"><button onClick={() => update((d) => delDetail(d, line.id, dd.id))} aria-label="Supprimer">✕</button></td>
          </tr>,
        );
      });
      rows.push(
        <tr key={`${line.id}-add`} className="atl-addrow">
          <td className="l" colSpan={addSpan}>
            <button className="atl-add-detail" onClick={() => update((d) => addDetail(d, line.id))}>＋ {t('scenarii:atterrissage.addDetail', 'Ajouter un détail')}</button>
          </td>
        </tr>,
      );
    }
    return rows;
  };

  // Card INDUCTEURS (sous le CA) : dérivée des détails byDriver ; qté éditable en annuel.
  const renderDriverRow = ({ lineId, did, dd }) => {
    const pct = isPctDetail(dd);
    // Les inducteurs classiques restent des PRESTATIONS (jours-hommes, EPT…) :
    // prix seul. Les produits (prix + coût de revient + volumes) vivent dans la
    // section Plan produits — plus de coût ni de liaison produit ici.
    const meta = pct
      ? <span className="atl-drvline">% de {lineLabel(dd.baseLineId) || '—'} : <Num value={dd.prix} onCommit={(v) => update((d) => setDetailField(d, lineId, did, 'prix', Number(v)))} />%</span>
      : <span className="atl-drvline">{unitLabel(dd.driver) || t('scenarii:atterrissage.driverName', 'Inducteur')} : CHF <Num value={dd.prix} onCommit={(v) => update((d) => setDetailField(d, lineId, did, 'prix', Math.round(v)))} /></span>;
    return (
      <tr key={did} className="atl-line">
        {nameCell(<>
          <span className="atl-linename">{dd.name || dd.driver || t('scenarii:atterrissage.revLabelPh', 'Revenu')}</span>
          {meta}
        </>, <button className="atl-pencil" onClick={() => openEditDrv(lineId, dd)} aria-label={t('scenarii:atterrissage.edit', 'Modifier')}>✎</button>)}
        {showPrev && <td className="atl-filet-r" />}
        {isMonthly ? (
          <>
            {MONTHS.map((_, i) => (i < closed ? <React.Fragment key={i}>{rCell(detailMonth(model, dd, focusYear, i))}</React.Fragment> : null))}
            {showReal && rCell(detailRealized(model, dd), 'atl-filet-l atl-filet-r')}
            {MONTHS.map((_, i) => (i >= closed ? (
              pct
                ? <React.Fragment key={i}>{rCell(detailMonth(model, dd, focusYear, i))}</React.Fragment>
                : <React.Fragment key={i}>{eCell(qtyFromCa(dd, detailMonth(model, dd, focusYear, i)), (v) => update((d) => setDriverPlanMonth(d, lineId, did, focusYear, i, v)), '', false, 1,
                  (vals) => update((d) => vals.forEach((val, k) => { if (i + k < 12 && i + k >= closed) setDriverPlanMonth(d, lineId, did, focusYear, i + k, val); })))}</React.Fragment>
            ) : null))}
            {closed > 0 && rCell(MONTHS.reduce((s, _, i) => (i >= closed ? s + detailMonth(model, dd, focusYear, i) : s), 0), 'atl-filet-l')}
            {rCell(detailAnnual(model, dd, focusYear), 'atl-filet-l')}
          </>
        ) : (
          <>
            {showReal && (pct
              ? rCell(detailRealized(model, dd), 'atl-filet-r')
              : eCell(qtyFromCa(dd, detailRealized(model, dd)),
                (v) => update((d) => { const x = lineEntry(d, lineId).details.find((z) => z.id === did); if (x) { x.caReal = (Number(x.prix) || 0) * (Number(v) || 0) / 1000; delete x.caRealM; } }), 'atl-filet-r', false, 1))}
            {years.map((yy, yi) => {
              // Volume EFFECTIF de l'année : plan mensuel (qtyM) prioritaire
              // (+ volume réalisé déduit pour l'année en cours), sinon qty[an].
              const qm = dd.qtyM && dd.qtyM[yy];
              const volEff = qm
                ? (yy === cur ? qtyFromCa(dd, driverRealized(dd)) : 0) + Object.values(qm).reduce((s, q) => s + (Number(q) || 0), 0)
                : (dd.qty?.[yy] ?? '');
              return <React.Fragment key={yy}>{pct
                ? rCell(detailAnnual(model, dd, yy))
                : eCell(volEff, (v) => update((d) => setDetailQty(d, lineId, did, yy, v)), '', false, 1,
                  (vals) => update((d) => vals.forEach((val, k) => { const y2 = years[yi + k]; if (y2 != null) setDetailQty(d, lineId, did, y2, val); })))}</React.Fragment>;
            })}
          </>
        )}
        <td className="atl-del" />
      </tr>
    );
  };

  // Card VARIABLES : taux réel (compta) + taux futur par année ; toujours en années.
  // Taux annuel PONDÉRÉ par les CA de la base : (taux réel × base réelle + taux
  // futur × base des mois à venir) / base totale. Pour N+1 : taux futur seul.
  const gRealized = () => lines.reduce((s, l) => s + LR(l.id), 0);
  const gYear = (y) => lines.reduce((s, l) => s + LA(l.id, y), 0);
  const gMonth = (y, i) => lines.reduce((s, l) => s + LM(l.id, y, i), 0);

  // Sans droit de lecture : rien. Le serveur refuse déjà (403) ; l'écran ne doit
  // pas afficher une copie locale de ce qu'il n'a pas le droit de montrer.
  /* Export Excel — section SANS onglet : le classeur est bâti à partir des
     tableaux DÉJÀ rendus (`exportRef` de PageShell). On ne remonte pas la page
     hors écran : elle enregistre au fil de la frappe, un second montage serait
     un risque d'écriture. */
  const exportRef = useRef(null);
  const excel = useViewExcelExport({
    ref: exportRef,
    sheetName: titre,
    fileBase: titre,
    tenantName: activeTenant?.name,
    year: cur,
    addToast,
  });
  const exportOptions = useMemo(
    () => [{ id: 'excel', label: 'Excel', run: excel.run }],
    [excel.run],
  );

  useRegisterPageExports(exportOptions);

  if (!canRead) {
    return (
      <PageShell timeless className="page-shell--flush" title={t('scenarii:atterrissage.title', 'Valeur ajoutée planifiée')}>
        <div className="atl-empty">
          {t('scenarii:atterrissage.noAccess', "Vous n'avez pas accès au périmètre Ventes.")}
        </div>
      </PageShell>
    );
  }
  /* Facturation et CA reconnu par card (2026-09-03) : une ligne par card du
     Plan services et du Plan produits, « Modèle - libellé », dépliable sur ses
     lignes ; même gabarit et même vue que la table du CA. `metric` = 'billed'
     (ce que la card facture, selon ses conditions) ou 'revenue' (ce qu'elle
     reconnaît en chiffre d'affaires). */
  const cardsTable = (metric) => {
        const cards = [
          ...servicePlanOf(model).map((c) => ({ c, svc: true })),
          ...productPlanOf(model).map((c) => ({ c, svc: false })),
        ];
        if (!cards.length) return null;
        const rowAt = ({ c, svc }, r, y, m) => {
          if (svc) {
            if (metric === 'billed' && c.kind === 'prepaid') return prepaidBilledAt(c, r, y, m);
            if (metric === 'billed' && c.kind === 'hourbank') return hourbankBilledMonth(model, c, r, y, m);
            return serviceRowRevenueMonth(model, c, r, y, m);
          }
          if (c.kind === 'trade') return tradeRowCaMonth(model, c, r, y, m);
          return productRowQtyMonth(r, y, m) * productRowPriceAt(model, r, y) / 1000;
        };
        const cardAt = ({ c, svc }, y, m) => (metric === 'billed'
          ? cardBilledMonth(model, c, svc, y, m)
          : (svc ? serviceCardRevenueMonth(model, c, y, m) : productCardRevenueMonth(model, c, y, m)));
        /* Prévision seule : les mois clos n'apparaissent pas, le réel est chez
           Finance. En vue mensuelle, les mois ouverts de l'année lue ; en vue
           pluriannuelle, l'année en cours ne compte que ses mois ouverts. */
        const blocks = yearsShown.map((y) => ({ y, months: MONTHS.map((_, m) => m).filter((m) => m >= closedY(y)) }));
        const cols = isMonthly ? blocks.flatMap((b2) => b2.months.map((m) => ({ y: b2.y, m }))) : years;
        const closedOfYear = (yy) => (yy === cur ? closedOf(model, cur) : 0);
        const serie = (f) => cols.map((col) => (isMonthly
          ? f(col.y, col.m)
          : MONTHS.reduce((s2, _, m) => (m >= closedOfYear(col) ? s2 + f(col, m) : s2), 0)));
        const rows = cards.map((x) => ({
          x,
          vals: serie((y, m) => cardAt(x, y, m)),
          subs: (x.c.rows || []).map((r) => ({ r, vals: serie((y, m) => rowAt(x, r, y, m)) })),
        }));
        const tot = (i2) => rows.reduce((s2, r) => s2 + r.vals[i2], 0);
        /* « Modèle - libellé » : Prestations - Grand comptes, Carnet d'heures - REYL… */
        const KIND_LBL = {
          presta: t('scenarii:servicePlan.kPresta', 'Prestations'),
          hourbank: t('scenarii:servicePlan.kHourbank', "Carnet d'heures"),
          prepaid: t('scenarii:servicePlan.kPrepaid', 'Service préfacturé'),
          recurring: t('scenarii:servicePlan.kRecurring', 'Récurrent / location'),
          trade: t('scenarii:atterrissage.kTradeShort', 'Négoce'),
        };
        const cardLbl = ({ c, svc }) => `${KIND_LBL[c.kind] || (svc ? t('scenarii:servicePlan.owned', 'Plan services') : t('scenarii:productPlan.kUnitsShort', 'Produits'))} - ${c.name || t('scenarii:productPlan.category', 'Catégorie')}`;
        const valCells = (vals) => {
          if (!isMonthly) return <>{vals.map((v, i2) => <td key={i2} className="atl-num">{fmt(v * scale)}</td>)}</>;
          let k = 0;
          return (
            <>
              {blocks.map((b2) => {
                const part = vals.slice(k, k + b2.months.length); k += b2.months.length;
                return (
                  <React.Fragment key={b2.y}>
                    {part.map((v, i2) => <td key={i2} className="atl-num">{fmt(v * scale)}</td>)}
                    <td className="atl-filet-l atl-num">{fmt(part.reduce((s2, v) => s2 + v, 0) * scale)}</td>
                  </React.Fragment>
                );
              })}
            </>
          );
        };
        const width = nameW + COLW.rule + COLW.del + (isMonthly
          ? blocks.reduce((s2, b2) => s2 + b2.months.length * COLW.mo + COLW.tot, 0) : years.length * COLW.yr);
        const colGroup = (
          <colgroup>
            <col className="atl-col-name" style={{ width: nameW }} />
            <col style={{ width: COLW.rule }} />
            {isMonthly ? blocks.map((b2) => (
              <React.Fragment key={b2.y}>
                {b2.months.map((m) => <col key={m} style={{ width: COLW.mo }} />)}
                <col style={{ width: COLW.tot }} />
              </React.Fragment>
            )) : years.map((yy) => <col key={yy} style={{ width: COLW.yr }} />)}
            <col className="atl-col-del" />
          </colgroup>
        );
        const head = (
          <>
            {isMonthly && (
              <tr className="atl-headgroup">
                <th className="l" /><th />
                {blocks.map((b2) => (
                  <React.Fragment key={b2.y}>
                    <th colSpan={b2.months.length} className="atl-grp">{`${t('scenarii:atterrissage.forecast', 'prévisionnel')} ${b2.y}`}</th>
                    <th className="atl-filet-l" />
                  </React.Fragment>
                ))}
                <th />
              </tr>
            )}
            <tr>
              <NameTh>{t('scenarii:atterrissage.cardCol', 'Card')}</NameTh>
              {isMonthly ? blocks.map((b2) => (
                <React.Fragment key={b2.y}>
                  {b2.months.map((m) => <th key={m}>{MONTHS[m]}</th>)}
                  <th className="atl-filet-l">{t('scenarii:atterrissage.toGo', 'À venir')}<br /><span className="atl-sub">{t('scenarii:atterrissage.nMonths', '{{n}} mois', { n: b2.months.length })}</span></th>
                </React.Fragment>
              )) : years.map((yy) => <th key={yy}>{yy}<br /><span className="atl-sub">{yy === cur ? t('scenarii:atterrissage.subForecast', 'prév.') : t('scenarii:atterrissage.subPlan', 'plan')}</span></th>)}
              <th />
            </tr>
          </>
        );
        const opened_ = metric === 'billed' ? billOpen : revOpen;
        const setOpened = metric === 'billed' ? setBillOpen : setRevOpen;
        const toggleBill = (id) => setOpened((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
        return (
          <Card title={metric === 'billed' ? t('scenarii:atterrissage.billingCard', 'Facturation') : t('scenarii:atterrissage.revenueCard', 'CA reconnu')}
                note={metric === 'billed' ? t('scenarii:atterrissage.billingNote', 'par card, selon ses conditions de facturation') : t('scenarii:atterrissage.revenueNote', 'par card, selon sa règle de reconnaissance')}>
            <div className="atl-scroll lk-hscroll">
              <table className="atl-table atl-table--fixed" style={{ width }}>
                {colGroup}
                <thead>{head}</thead>
                <tbody>
                  {rows.map(({ x, vals, subs }) => {
                    const opened = opened_.has(x.c.id);
                    const btn = <button className={`atl-tgl-btn${opened ? ' is-open' : ''}`} onClick={() => toggleBill(x.c.id)} aria-label={opened ? 'Réduire' : 'Développer'}>{opened ? '−' : '+'}</button>;
                    return (
                      <React.Fragment key={x.c.id}>
                        <tr className="atl-line">
                          {nameCell(<span className="atl-linename">{cardLbl(x)}</span>, btn)}
                          {valCells(vals)}
                          <td />
                        </tr>
                        {opened && subs.map(({ r, vals: sv }, i2) => (
                          <tr key={r.id || i2} className="atl-detail">
                            {nameCell(<span className="atl-detname atl-detname--sub">{r.name || '—'}</span>, null, 'atl-detail__name')}
                            {valCells(sv)}
                            <td />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  <tr className="atl-tot">
                    {nameCell(metric === 'billed' ? t('scenarii:atterrissage.billingTotal', 'Total facturé') : t('scenarii:atterrissage.revenueTotal', 'Total CA reconnu'), null)}
                    {valCells(cols.map((_, i2) => tot(i2)))}
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        );
  };



  return (
    <AtlReadOnlyCtx.Provider value={!canWrite}>
    <PageShell framed exportRef={exportRef} title={titre} unit={pageUnit}>
      <Tabs tabs={onglets.map((id) => ({ id, label: {
                     plan: t('scenarii:atterrissage.tabPlan', 'Plan des ventes'),
                     forecast: t('scenarii:atterrissage.tabForecast', 'Prévision'),
                     services: t('scenarii:servicePlan.title', 'Plan services'),
                     costs: t('nav:items.direct-costs', 'Coûts des ventes'),
                     margins: t('scenarii:atterrissage.tabMargins', 'Revenus & Marges'),
                     receivables: t('scenarii:atterrissage.tabReceivables', 'Débiteurs') }[id] }))}
            active={hubTab} onChange={setHubTab} />
      {hubTab === 'services' && <ServicePlanPage activeTenant={activeTenant} addToast={addToast} canRead={canRead} canWrite={canWrite} embedded />}
      {hubTab === 'costs' && <DirectCostsPage activeTenant={activeTenant} activeYear={y0} canWrite={canWriteCosts} unit={unit} embedded />}
      {hubTab === 'margins' && <RevenueMarginsPage activeTenant={activeTenant} year={y0} unit={unit} canWrite={canWriteCosts} />}
      {hubTab === 'receivables' && <SalesReceivablesPage activeTenant={activeTenant} year={y0} unit={unit} />}
      {hubTab === 'forecast' && (
        <>
          <div className="atl-toolbar">{seg()}</div>
          {cardsTable('billed')}
          {cardsTable('revenue')}
        </>
      )}
      {hubTab === 'plan' && !canWrite && (
        <div className="atl-readonly">
          {t('scenarii:atterrissage.readOnly', 'Lecture seule — votre rôle ne permet pas de modifier le plan des ventes.')}
        </div>
      )}
      {hubTab === 'plan' && (
      <div className="atl-toolbar">
        {seg()}
        {canWrite && (
        <button className="atl-copybtn atl-rulebtn" onClick={openNewVar}>
          ＋ {t('scenarii:atterrissage.addRule', 'Ajouter un variable')}
        </button>
        )}
        {canWrite && (
        <button className="atl-copybtn" onClick={() => setCopyModal({ sel: Object.fromEntries(lines.map((l) => [l.id, true])), dc: true })}>
          {t('scenarii:atterrissage.copyYear', 'Copier {{from}} → {{to}}', { from: cur, to: cur + 1 })}
        </button>
        )}
        {(() => {
          // Lignes qui ont VRAIMENT un chevron (détails ou possédées par un
          // plan) — seules concernées par « Tout replier/déplier ». Le niveau
          // plus profond (sous-lignes par client, `openPlans`) garde son
          // propre état, comme les autres boutons « Tout replier » du module
          // qui ne gèrent que le niveau card/ligne, pas les sous-niveaux.
          const collapsible = lines.filter((l) => detailsOf(model, l.id).length > 0 || prodLines.has(l.id));
          if (!collapsible.length) return null;
          const allOpen = collapsible.every((l) => isOpen(l.id, detailsOf(model, l.id).length > 0 || prodLines.has(l.id)));
          return (
            <button className="atl-copybtn atl-collapsebtn" onClick={() => setOpen(
              Object.fromEntries(collapsible.map((l) => [l.id, !allOpen])),
            )}>
              {allOpen ? t('scenarii:servicePlan.collapseAll', 'Tout replier') : t('scenarii:servicePlan.expandAll', 'Tout déplier')}
            </button>
          );
        })()}
      </div>

      )}

      {hubTab === 'plan' && (
      <Card title={t('scenarii:atterrissage.total', 'Chiffre d\'affaires')} note={unit === 'chf' ? t('scenarii:atterrissage.inChf', 'en CHF') : t('scenarii:atterrissage.inKchf', 'en milliers de CHF')}>
        {revLines == null ? (
          <div className="atl-empty">{t('scenarii:atterrissage.loading', 'Chargement des lignes Finance…')}</div>
        ) : lines.length === 0 ? (
          <div className="atl-empty">{t('scenarii:atterrissage.noLines', 'Aucune ligne de revenus Finance pour ce client.')}</div>
        ) : (
          <div className="atl-scroll lk-hscroll">
            <table className="atl-table atl-table--fixed" style={{ width: tableW }}>
              <ColGroup />
              <thead>
                <GroupHead />
                <tr><NameTh>{t('scenarii:atterrissage.line', 'Ligne de revenus')}</NameTh><ValHead /></tr>
              </thead>
              <tbody>
                {lines.map(renderLine)}
                <tr className="atl-tot">
                  {nameCell(t('scenarii:atterrissage.total', 'Chiffre d\'affaires'), null)}
                  {isMonthly ? yearsShown.map((y) => {
                    const cy = closedY(y);
                    return (
                      <React.Fragment key={y}>
                        {MONTHS.map((_, i) => (i < cy ? <td key={i} className="atl-num">{fmt(gMonth(y, i) * scale)}</td> : null))}
                        {cy > 0 && <td className="atl-filet-l atl-filet-r atl-num">{fmt(gRealized() * scale)}</td>}
                        {MONTHS.map((_, i) => (i >= cy ? <td key={i} className="atl-num">{fmt(gMonth(y, i) * scale)}</td> : null))}
                        {cy > 0 && <td className="atl-filet-l atl-num">{fmt(MONTHS.reduce((s0, _, i) => (i >= cy ? s0 + gMonth(y, i) : s0), 0) * scale)}</td>}
                        <td className="atl-filet-l atl-num">{fmt(gYear(y) * scale)}</td>
                      </React.Fragment>
                    );
                  }) : (
                    <>
                      {showPrev && <td className="atl-filet-r atl-num">{fmt(lines.reduce((sm, l) => sm + prevReal(l.id), 0) * scale)}</td>}
                      {showReal && <td className="atl-filet-r atl-num">{fmt(gRealized() * scale)}</td>}
                      {years.map((yy) => <td key={yy} className="atl-num">{fmt(gYear(yy) * scale)}</td>)}
                    </>
                  )}
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {indMenu && <div className="atl-menu-ov" onClick={() => setIndMenu(null)} />}
      {drvModal && (
        <div className="atl-modal-ov" onClick={() => setDrvModal(null)}>
          <div className="atl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('scenarii:atterrissage.editDriver', 'Modifier l\'inducteur')}</h3>
            <div className="atl-modal__line">{t('scenarii:atterrissage.revLine', 'Ligne de revenus')} : <b>{lineLabel(drvModal.lineId)}</b></div>
            <label>{t('scenarii:atterrissage.revLabel', 'Libellé revenu')}
              <input value={drvModal.form.name} placeholder={t('scenarii:atterrissage.revLabelPh', 'Revenu')} onChange={(e) => setForm('name', e.target.value)} />
            </label>
            {drvModal.dmode === 'pct' ? (
              <>
                <label>{t('scenarii:atterrissage.baseLine', 'Ligne de base (le %)')}
                  <select value={drvModal.form.baseLineId} onChange={(e) => setForm('baseLineId', e.target.value)}>
                    <option value="">{t('scenarii:atterrissage.choose', '— choisir —')}</option>
                    {lines.filter((l) => l.id !== drvModal.lineId).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </label>
                <label>{t('scenarii:atterrissage.pctValue', 'Pourcentage (%)')}
                  <input type="number" value={drvModal.form.prix} onChange={(e) => setForm('prix', e.target.value)} />
                </label>
              </>
            ) : (
              <>
                <label>{t('scenarii:atterrissage.driverName', 'Inducteur')}
                  <input value={drvModal.form.driver} placeholder={t('scenarii:atterrissage.driverPh', 'jours-hommes…')} onChange={(e) => setForm('driver', e.target.value)} />
                </label>
                <label>{t('scenarii:atterrissage.unitPrice', 'Prix unit. (CHF)')}
                  <input type="number" value={drvModal.form.prix} onChange={(e) => setForm('prix', e.target.value)} />
                </label>
              </>
            )}
            <div className="atl-modal__actions">
              <button className="atl-modal__del" onClick={() => { update((d) => detachDriver(d, drvModal.lineId, drvModal.did)); setDrvModal(null); }}>{t('scenarii:atterrissage.removeInducteur', 'Retirer l\'inducteur')}</button>
              <span style={{ flex: 1 }} />
              <button className="atl-modal__cancel" onClick={() => setDrvModal(null)}>{t('scenarii:atterrissage.cancel', 'Annuler')}</button>
              <button className="atl-modal__save" onClick={saveDrv}>{t('scenarii:atterrissage.save', 'Enregistrer')}</button>
            </div>
          </div>
        </div>
      )}

      {varModal && (
        <div className="atl-modal-ov" onClick={() => setVarModal(null)}>
          <div className="atl-modal atl-modal--wide" onClick={(e) => e.stopPropagation()}>
            <h3>{varModal.id ? t('scenarii:atterrissage.editVar', 'Modifier la règle') : t('scenarii:atterrissage.newVar', 'Nouvelle règle')}</h3>
            <div className="atl-modal__row">
              <label>{t('scenarii:atterrissage.revLabel', 'Libellé revenu')}
                <input value={varModal.form.name} placeholder={t('scenarii:atterrissage.revLabelPh', 'Revenu')} onChange={(e) => setVarForm('name', e.target.value)} />
              </label>
              <label>{t('scenarii:atterrissage.targetLine', 'Ligne cible (alimentée)')}
                <select value={varModal.form.targetRef} onChange={(e) => setVarForm('targetRef', e.target.value)}>
                  <option value="">{t('scenarii:atterrissage.choose', '— choisir —')}</option>
                  {lines.map((l) => (
                    <optgroup key={l.id} label={l.label}>
                      <option value={`line:${l.id}`}>{l.label} — {t('scenarii:atterrissage.wholeLine', 'toute la ligne')}</option>
                      <option value={`adj:${l.id}`}>{adjLabel(l.id)}</option>
                    </optgroup>
                  ))}
                </select>
              </label>
              <label>{t('scenarii:atterrissage.baseLine2', 'Ligne de base (le %)')}
                <select value={varModal.form.baseRef} onChange={(e) => setVarForm('baseRef', e.target.value)}>
                  <option value="">{t('scenarii:atterrissage.choose', '— choisir —')}</option>
                  {lines.filter((l) => l.id !== parseRef(varModal.form.targetRef).lineId).map((l) => (
                    <optgroup key={l.id} label={l.label}>
                      <option value={`line:${l.id}`}>{l.label} — {t('scenarii:atterrissage.wholeLine', 'toute la ligne')}</option>
                      {fin && <option value={`adj:${l.id}`}>{adjLabel(l.id)}</option>}
                      {detailsOf(model, l.id).map((dd) => <option key={dd.id} value={`det:${l.id}:${dd.id}`}>{dd.name || t('scenarii:atterrissage.detailPh', 'Détail')}</option>)}
                    </optgroup>
                  ))}
                </select>
              </label>
            </div>
            {/* Le RÉEL guide le taux : évolution N-1 puis mois clos de l'année. */}
            {varModal.form.targetRef && varModal.form.baseRef && (() => {
              const vv = { targetRef: varModal.form.targetRef, baseRef: varModal.form.baseRef };
              // Moyenne N-1 · N réel (mois clos) · à venir (le taux posé) ·
              // N total (pondéré réel + futur par les bases).
              const p0 = realRatePrev(vv);
              const rr0 = realRate(vv);
              const togo = parseFloat(String(varModal.form.rates?.[cur] ?? '').replace(',', '.'));
              const rTogo = Number.isFinite(togo) ? togo : null;
              let blended = null;
              if (fin && rr0 != null && rTogo != null) {
                const bR = baseReal(vv.baseRef);
                let bF = 0; for (let m = curClosed; m < 12; m++) bF += baseMonthNv(vv.baseRef, cur, m);
                if (bR + bF) blended = Math.round((rr0 * bR + rTogo * bF) / (bR + bF) * 10) / 10;
              }
              const chips = [
                [`Ø ${cur - 1}`, p0],
                [`${cur} ${t('scenarii:atterrissage.obsReal', 'réel')} (${curClosed} ${t('scenarii:atterrissage.nMonthsShort', 'mois')})`, rr0],
                [t('scenarii:atterrissage.obsTogo', 'À venir'), rTogo],
                [`${cur} ${t('scenarii:atterrissage.obsTotal', 'total')}`, blended],
              ].filter(([, v2]) => v2 != null);
              if (!chips.length) return null;
              return (
                <div className="atl-obs">
                  <span className="atl-obs__t">{t('scenarii:atterrissage.realRateObs', 'Taux réel observé')}</span>
                  {chips.map(([lbl, val]) => (
                    <span key={lbl} className="atl-obs__chip">{lbl} <b>{`${val} %`}</b></span>
                  ))}
                </div>
              );
            })()}
            <div className="atl-modal__line atl-ratesrow-lbl">{t('scenarii:atterrissage.ratePerYear', 'Taux par année (une année vide reprend le taux précédent)')}</div>
            <div className="atl-ratesrow">
              {years.map((yy) => (
                <label key={yy} className="atl-rateyear">
                  <span>{yy}</span>
                  <span className="atl-rateyear__field">
                    <input
                      inputMode="decimal" placeholder="–"
                      value={varModal.form.rates?.[yy] ?? ''}
                      onChange={(e) => setVarModal((m) => ({ ...m, form: { ...m.form, rates: { ...m.form.rates, [yy]: e.target.value } } }))} />
                    <span className="atl-pct">%</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="atl-modal__actions">
              {varModal.id && <button className="atl-modal__del" onClick={() => { update((d) => delVariable(d, varModal.id)); setVarModal(null); }}>{t('scenarii:atterrissage.delete', 'Supprimer')}</button>}
              <span style={{ flex: 1 }} />
              <button className="atl-modal__cancel" onClick={() => setVarModal(null)}>{t('scenarii:atterrissage.cancel', 'Annuler')}</button>
              <button className="atl-modal__save" disabled={!varModal.form.targetRef || !varModal.form.baseRef} onClick={saveVar}>{t('scenarii:atterrissage.save', 'Enregistrer')}</button>
            </div>
          </div>
        </div>
      )}

      {copyModal && (
        <div className="atl-modal-ov" onClick={() => setCopyModal(null)}>
          <div className="atl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('scenarii:atterrissage.copyYear', 'Copier {{from}} → {{to}}', { from: cur, to: cur + 1 })}</h3>
            <div className="atl-modal__line">{t('scenarii:atterrissage.copyHint2', 'Lignes de CA à copier — inducteurs (volumes) et variables (taux) inclus. Les coûts directs se copient mois par mois.')}</div>
            {lines.map((l) => (
              <label key={l.id} className="atl-copyline">
                <input type="checkbox" checked={!!copyModal.sel[l.id]}
                  onChange={(e) => setCopyModal((m) => ({ sel: { ...m.sel, [l.id]: e.target.checked } }))} />
                <span>{l.label}</span>
              </label>
            ))}
            <label className="atl-copyline atl-copyline--dc">
              <input type="checkbox" checked={!!copyModal.dc}
                onChange={(e) => setCopyModal((m) => ({ ...m, dc: e.target.checked }))} />
              <span>{t('finance:directCosts.title', 'Coûts directs')}</span>
            </label>
            <div className="atl-modal__actions">
              <button className="atl-modal__cancel" onClick={() => setCopyModal(null)}>{t('scenarii:atterrissage.cancel', 'Annuler')}</button>
              <span style={{ flex: 1 }} />
              <button className="atl-modal__save" disabled={!Object.values(copyModal.sel).some(Boolean) && !copyModal.dc}
                onClick={() => {
                  const ids = lines.filter((l) => copyModal.sel[l.id]).map((l) => l.id);
                  if (ids.length) update((d) => copyYearForward(d, ids));
                  if (copyModal.dc) copyDirectCosts(cur, cur + 1);
                  setCopyModal(null);
                }}>
                {t('scenarii:atterrissage.copy', 'Copier')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* La table « Coûts directs » a quitté cette page le 2026-09-01 : elle a
          sa propre section (Ventes & Ops › Coûts des ventes) depuis que le
          menu la porte, et deux endroits pour la même table, c'est un jour où
          l'on ne sait plus laquelle fait foi. L'allocation des coûts de cards
          reste ici : elle DÉSIGNE une ligne de charges directes, elle ne la
          saisit pas. */}
    </PageShell>
    </AtlReadOnlyCtx.Provider>
  );

  function seg() {
    return (
      <SegmentedToggle compact label={t('scenarii:atterrissage.period', 'Période')} value={win} onChange={setWin}
                       options={[{ value: '1', label: `${cur}` }, { value: '2', label: `${cur} · ${cur + 1}` }]} />
    );
  }
}
